import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, FileText, Download, Truck, Package, Printer, MapPin, XCircle, Loader2, Share2, Smartphone, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Semua Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
];

export default function TenantOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const { toast } = useToast();

  const fetchOrders = async () => {
    const res = await api.get('/tenant/orders', { params: { status: statusFilter === 'all' ? '' : statusFilter } });
    setOrders(res.data.orders); setTotal(res.data.total);
  };
  useEffect(() => { fetchOrders(); }, [statusFilter]);

  const [shippingLoading, setShippingLoading] = useState('');
  const [trackingData, setTrackingData] = useState(null);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/tenant/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
      if (selected?.id === orderId) {
        const updates = { status: newStatus };
        if (newStatus === 'paid') updates.payment_status = 'paid';
        setSelected({ ...selected, ...updates });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal update status', variant: 'destructive' });
    }
  };

  const updatePaymentStatus = async (orderId, newPaymentStatus) => {
    try {
      await api.put(`/tenant/orders/${orderId}/payment-status`, { status: newPaymentStatus });
      fetchOrders();
      if (selected?.id === orderId) {
        const updates = { payment_status: newPaymentStatus };
        if (newPaymentStatus === 'paid' && (selected.status === 'pending' || selected.status === 'pending_confirmation')) updates.status = 'paid';
        setSelected({ ...selected, ...updates });
      }
      toast({ title: 'Berhasil', description: `Status pembayaran diubah ke ${paymentStatusLabel(newPaymentStatus)}` });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal update status pembayaran', variant: 'destructive' });
    }
  };

  const createShipment = async (orderId) => {
    setShippingLoading('create');
    try {
      const res = await api.post(`/tenant/orders/${orderId}/create-shipment`);
      toast({ title: 'Berhasil', description: res.data.message });
      fetchOrders();
      if (selected?.id === orderId) setSelected({ ...selected, komship_order_id: res.data.komship_order_id, tracking_number: res.data.awb, shipping_status: 'booked', status: 'processing' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal membuat pengiriman', variant: 'destructive' });
    } finally { setShippingLoading(''); }
  };

  const cancelShipment = async (orderId) => {
    setShippingLoading('cancel');
    try {
      await api.put(`/tenant/orders/${orderId}/cancel-shipment`);
      toast({ title: 'Berhasil', description: 'Pengiriman dibatalkan' });
      fetchOrders();
      if (selected?.id === orderId) setSelected({ ...selected, komship_order_id: null, tracking_number: null, shipping_status: 'canceled' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal membatalkan pengiriman', variant: 'destructive' });
    } finally { setShippingLoading(''); }
  };

  const requestPickup = async (orderId) => {
    setShippingLoading('pickup');
    try {
      await api.post(`/tenant/orders/${orderId}/request-pickup`);
      toast({ title: 'Berhasil', description: 'Request pickup berhasil' });
      fetchOrders();
      if (selected?.id === orderId) setSelected({ ...selected, shipping_status: 'pickup_requested' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal request pickup', variant: 'destructive' });
    } finally { setShippingLoading(''); }
  };

  const printLabel = async (orderId) => {
    setShippingLoading('label');
    try {
      const res = await api.post(`/tenant/orders/${orderId}/print-label`);
      const labelUrl = res.data?.data?.label_url || res.data?.data?.url;
      if (labelUrl) window.open(labelUrl, '_blank');
      else toast({ title: 'Info', description: 'Label data received, check console' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal cetak label', variant: 'destructive' });
    } finally { setShippingLoading(''); }
  };

  const loadTracking = async (orderId) => {
    setShippingLoading('tracking');
    try {
      const res = await api.get(`/tenant/orders/${orderId}/tracking`);
      setTrackingData(res.data);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal mendapatkan tracking', variant: 'destructive' });
    } finally { setShippingLoading(''); }
  };

  const downloadPdf = async (orderId, orderNumber) => {
    try {
      const response = await api.get(`/tenant/orders/${orderId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Berhasil', description: 'Invoice berhasil diunduh' });
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal mengunduh invoice', variant: 'destructive' });
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const statusColor = (s) => {
    const map = { pending: 'bg-amber-100 text-amber-700', unpaid: 'bg-amber-100 text-amber-700', pending_confirmation: 'bg-blue-100 text-blue-700', paid: 'bg-emerald-100 text-emerald-700', processing: 'bg-blue-100 text-blue-700', shipped: 'bg-indigo-100 text-indigo-700', delivered: 'bg-teal-100 text-teal-700', completed: 'bg-emerald-100 text-emerald-700', canceled: 'bg-red-100 text-red-700', refunded: 'bg-slate-100 text-slate-700' };
    return map[s] || 'bg-slate-100 text-slate-700';
  };
  const shippingStatusLabel = (s) => {
    const map = { booked: 'Dipesan', pickup_requested: 'Menunggu Pickup', picked_up: 'Dipickup', in_transit: 'Dalam Perjalanan', delivered: 'Terkirim', returned: 'Dikembalikan', failed: 'Gagal', canceled: 'Dibatalkan' };
    return map[s] || s || '-';
  };

  const paymentStatusLabel = (s) => {
    const map = { unpaid: 'Belum Bayar', pending: 'Menunggu', pending_confirmation: 'Menunggu Konfirmasi', paid: 'Lunas', refunded: 'Refund' };
    return map[s] || s || '-';
  };

  return (
    <div data-testid="tenant-orders-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Pesanan ({total})</h1>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="order-status-filter"><SelectValue placeholder="Filter status" /></SelectTrigger>
          <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead><tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Order</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Customer</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Total</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Payment</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Pengiriman</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Tanggal</th>
                <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
              </tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-4 font-mono text-xs">
                      {o.order_number}
                      {o.is_ppob && <Badge variant="outline" className="ml-1 bg-purple-50 text-purple-700 border-purple-200 text-[10px]">PPOB</Badge>}
                    </td>
                    <td className="py-3 px-4">
                      {o.customer_name}
                      {o.affiliate_code && (
                        <span className="ml-1 inline-flex items-center text-[10px] bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded-full" title={`Affiliate: ${o.affiliate?.name || o.affiliate_code}`}>
                          <Share2 size={8} className="mr-0.5" />{o.affiliate?.name || o.affiliate_code}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium">Rp {fmt(o.total)}</td>
                    <td className="py-3 px-4"><Badge variant="outline" className={statusColor(o.payment_status)}>{paymentStatusLabel(o.payment_status)}</Badge></td>
                    <td className="py-3 px-4"><Badge variant="outline" className={statusColor(o.status)}>{o.status}</Badge></td>
                    <td className="py-3 px-4 text-xs">{o.tracking_number ? <span className="font-mono text-indigo-600">{o.tracking_number}</span> : <span className="text-slate-400">{shippingStatusLabel(o.shipping_status)}</span>}</td>
                    <td className="py-3 px-4 text-slate-500 text-xs">{new Date(o.created_at).toLocaleDateString('id-ID')}</td>
                    <td className="py-3 px-4 text-right space-x-1">
                      <Button variant="ghost" size="icon" onClick={() => setSelected(o)} data-testid={`view-order-${o.id}`}><Eye size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => downloadPdf(o.id, o.order_number)} data-testid={`download-pdf-${o.id}`}><Download size={14} /></Button>
                    </td>
                  </tr>
                ))}
                {!orders.length && <tr><td colSpan={8} className="py-12 text-center text-slate-400">Belum ada pesanan</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setTrackingData(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detail Pesanan</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Order:</span> <span className="font-mono">{selected.order_number}</span></div>
                <div><span className="text-slate-500">Customer:</span> {selected.customer_name}</div>
                <div><span className="text-slate-500">Email:</span> {selected.customer_email}</div>
                <div><span className="text-slate-500">Total:</span> <span className="font-bold">Rp {fmt(selected.total)}</span></div>
                <div><span className="text-slate-500">Payment:</span> {selected.payment_method}</div>
                <div><span className="text-slate-500">Status Bayar:</span> <Badge variant="outline" className={statusColor(selected.payment_status)}>{paymentStatusLabel(selected.payment_status)}</Badge></div>
                <div><span className="text-slate-500">COD:</span> {selected.is_cod ? 'Ya' : 'Tidak'}</div>
                {selected.shipping_courier && <div><span className="text-slate-500">Kurir:</span> {selected.shipping_courier} {selected.shipping_service}</div>}
                {selected.destination_label && <div><span className="text-slate-500">Tujuan:</span> {selected.destination_label}</div>}
                {selected.affiliate_code && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Affiliate:</span>{' '}
                    <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 ml-1">
                      <Share2 size={10} className="mr-1" />{selected.affiliate?.name || selected.affiliate_code}
                    </Badge>
                    {selected.affiliate?.email && <span className="text-xs text-slate-400 ml-2">({selected.affiliate.email})</span>}
                  </div>
                )}
              </div>

              {/* PPOB Info */}
              {selected.is_ppob && selected.ppob_data && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-2">
                  <p className="text-sm font-medium text-purple-800 flex items-center gap-2"><Smartphone size={14} /> Info PPOB</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-purple-600">Produk:</span> {selected.ppob_data.product_name}</div>
                    <div><span className="text-purple-600">SKU:</span> <span className="font-mono">{selected.ppob_data.sku}</span></div>
                    <div><span className="text-purple-600">No. Tujuan:</span> <span className="font-mono font-medium">{selected.ppob_data.customer_number}</span></div>
                    <div><span className="text-purple-600">Status PPOB:</span>{' '}
                      <Badge variant="outline" className={
                        selected.ppob_data.ppob_status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                        selected.ppob_data.ppob_status === 'failed' ? 'bg-red-100 text-red-700' :
                        selected.ppob_data.ppob_status === 'processing' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }>{selected.ppob_data.ppob_status || 'Menunggu Pembayaran'}</Badge>
                    </div>
                    {selected.ppob_data.sn && (
                      <div className="col-span-2"><span className="text-purple-600">Serial Number (SN):</span> <span className="font-mono font-bold text-emerald-700">{selected.ppob_data.sn}</span></div>
                    )}
                    {selected.ppob_data.provider_ref && (
                      <div><span className="text-purple-600">Ref Tripay:</span> <span className="font-mono">{selected.ppob_data.provider_ref}</span></div>
                    )}
                    <div><span className="text-purple-600">Harga Dasar:</span> Rp {fmt(selected.ppob_data.base_price)}</div>
                    <div><span className="text-purple-600">Harga Jual:</span> Rp {fmt(selected.ppob_data.selling_price)}</div>
                    <div><span className="text-purple-600">Profit:</span> <span className="font-medium text-emerald-700">Rp {fmt(selected.ppob_data.profit)}</span></div>
                  </div>
                  {selected.ppob_data.callback_message && (
                    <div className="mt-2 p-2 bg-white/60 rounded text-xs text-slate-600">
                      <span className="font-medium">Pesan Tripay:</span> {selected.ppob_data.callback_message}
                    </div>
                  )}
                  {selected.ppob_data.tripay_message && selected.ppob_data.tripay_message !== selected.ppob_data.callback_message && (
                    <div className="p-2 bg-white/60 rounded text-xs text-slate-600">
                      <span className="font-medium">Keterangan:</span> {selected.ppob_data.tripay_message}
                    </div>
                  )}
                  {selected.ppob_data.ppob_status === 'failed' && (
                    <Button size="sm" variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-100"
                      onClick={async () => {
                        try {
                          await api.post(`/tenant/ppob/orders/${selected.id}/retry`);
                          toast({ title: 'PPOB', description: 'PPOB sedang diproses ulang...' });
                          fetchOrders();
                        } catch (err) {
                          toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal retry PPOB', variant: 'destructive' });
                        }
                      }}>
                      <RefreshCw size={14} className="mr-1" /> Proses Ulang PPOB
                    </Button>
                  )}
                </div>
              )}

              {/* Pending Payment Confirmation Notice */}
              {selected.payment_status === 'pending_confirmation' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-2">⏳ Menunggu Konfirmasi Pembayaran</p>
                  <p className="text-xs text-amber-600 mb-3">Customer telah mengirim bukti transfer. Verifikasi dan setujui pembayaran.</p>
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => { setSelected(null); navigate('/dashboard/payment-confirmations'); }}>
                    Lihat Konfirmasi Transfer
                  </Button>
                </div>
              )}

              {/* Shipping Info */}
              {(selected.komship_order_id || selected.tracking_number || selected.shipping_status) && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg space-y-2">
                  <p className="text-sm font-medium text-indigo-800 flex items-center gap-2"><Truck size={14} /> Info Pengiriman</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {selected.tracking_number && <div><span className="text-indigo-600">Resi:</span> <span className="font-mono font-medium">{selected.tracking_number}</span></div>}
                    {selected.shipping_status && <div><span className="text-indigo-600">Status:</span> {shippingStatusLabel(selected.shipping_status)}</div>}
                    {selected.is_cod && selected.cod_status && <div><span className="text-indigo-600">COD:</span> {selected.cod_status}</div>}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Items:</p>
                {(selected.items || []).map((item, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-50">
                    <span>{item.product_name} x{item.quantity}</span>
                    <span>Rp {fmt(item.total)}</span>
                  </div>
                ))}
              </div>

              {/* Shipping Actions - hide for PPOB */}
              {!selected.is_ppob && (
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Aksi Pengiriman:</p>
                <div className="flex flex-wrap gap-2">
                  {(selected.status === 'paid' || selected.status === 'processing') && !selected.komship_order_id && (
                    <Button size="sm" onClick={() => createShipment(selected.id)} disabled={shippingLoading === 'create'}>
                      {shippingLoading === 'create' ? <Loader2 size={14} className="animate-spin mr-1" /> : <Package size={14} className="mr-1" />} Buat Pengiriman
                    </Button>
                  )}
                  {selected.komship_order_id && !['delivered', 'returned', 'canceled'].includes(selected.shipping_status) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => requestPickup(selected.id)} disabled={shippingLoading === 'pickup'}>
                        {shippingLoading === 'pickup' ? <Loader2 size={14} className="animate-spin mr-1" /> : <MapPin size={14} className="mr-1" />} Request Pickup
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => printLabel(selected.id)} disabled={shippingLoading === 'label'}>
                        {shippingLoading === 'label' ? <Loader2 size={14} className="animate-spin mr-1" /> : <Printer size={14} className="mr-1" />} Cetak Label
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => cancelShipment(selected.id)} disabled={shippingLoading === 'cancel'}>
                        {shippingLoading === 'cancel' ? <Loader2 size={14} className="animate-spin mr-1" /> : <XCircle size={14} className="mr-1" />} Batalkan
                      </Button>
                    </>
                  )}
                  {selected.tracking_number && (
                    <Button size="sm" variant="outline" onClick={() => loadTracking(selected.id)} disabled={shippingLoading === 'tracking'}>
                      {shippingLoading === 'tracking' ? <Loader2 size={14} className="animate-spin mr-1" /> : <Truck size={14} className="mr-1" />} Lacak
                    </Button>
                  )}
                </div>
              </div>
              )}

              {/* Tracking Timeline */}
              {trackingData?.data && (
                <div className="p-3 bg-slate-50 border rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Tracking: {trackingData.tracking_number}</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(Array.isArray(trackingData.data) ? trackingData.data : trackingData.data.history || []).map((h, i) => (
                      <div key={i} className="flex gap-3 text-xs">
                        <span className="text-slate-400 whitespace-nowrap">{h.date || h.timestamp || ''}</span>
                        <span className="text-slate-700">{h.description || h.status || h.note || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Update Status:</p>
                <div className="flex flex-wrap gap-2">
                  {['paid', 'processing', 'shipped', 'delivered', 'completed', 'canceled'].filter(s => s !== selected.status).map(s => (
                    <Button key={s} size="sm" variant="outline" onClick={() => updateStatus(selected.id, s)} data-testid={`set-status-${s}`}>{s}</Button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Update Status Pembayaran:</p>
                <div className="flex flex-wrap gap-2">
                  {[{v: 'unpaid', l: 'Belum Bayar'}, {v: 'paid', l: 'Lunas'}, {v: 'pending_confirmation', l: 'Menunggu Konfirmasi'}, {v: 'refunded', l: 'Refund'}].filter(s => s.v !== selected.payment_status).map(s => (
                    <Button key={s.v} size="sm" variant="outline" className={s.v === 'paid' ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : s.v === 'refunded' ? 'border-red-300 text-red-700 hover:bg-red-50' : ''} onClick={() => updatePaymentStatus(selected.id, s.v)} data-testid={`set-payment-${s.v}`}>{s.l}</Button>
                  ))}
                </div>
              </div>
              <div className="pt-2 border-t">
                <Button onClick={() => downloadPdf(selected.id, selected.order_number)} className="w-full" data-testid="download-order-pdf">
                  <FileText className="h-4 w-4 mr-2" /> Download Invoice PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
